<script setup>
const route = useRoute();
const siteUrl = 'https://gefestai.com'; // Или useRuntimeConfig().public.apiBase
const categorySlug = route.params.category_slug;
const subSlug = route.params.sub_slug;

// Получаем данные субкатегории и ее статей
const { data: subcategory, pending, error } = await useFetch(`${siteUrl}/api/subcategory/${subSlug}`, {
    key: `subcategory-${subSlug}`,
    transform: (res) => res.data || res
});

// SEO из базы данных (используем meta_title и meta_description из таблицы subcategories)
useHead({
    link: [
        { rel: 'canonical', href: `${siteUrl}${route.path}` }
    ]
});

useSeoMeta({
    title: subcategory.value?.meta_title || subcategory.value?.name,
    description: subcategory.value?.meta_description || subcategory.value?.description,
    ogTitle: subcategory.value?.meta_title || subcategory.value?.name,
    ogDescription: subcategory.value?.meta_description || subcategory.value?.description,
});

const getTrendStatus = (views) => {
    if (!subcategory.value?.articles?.length) return false;

    // Вариант 1: Статичный порог (например, больше 500 просмотров)
    // return views > 500;

    // Вариант 2: Динамический (входим в топ по просмотрам в этом разделе)
    const allViews = subcategory.value.articles.map(a => Number(a.views || 0));
    const maxViews = Math.max(...allViews);

    // Если просмотров больше 80% от максимума и просмотры вообще есть
    return views > 0 && views >= maxViews * 0.8;
};

</script>

<template>
    <div class="min-h-screen bg-[#0F1113] text-gray-300 relative flex flex-col font-sans">

        <div v-if="pending" class="flex-grow flex items-center justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>

        <div v-else-if="error" class="flex-grow flex flex-col items-center justify-center">
            <p class="text-red-400 mb-4">Раздел не найден</p>
            <NuxtLink to="/blog" class="text-orange-500 underline">В базу знаний</NuxtLink>
        </div>

        <template v-else-if="subcategory">
            <nav class="relative z-20 px-6 py-6 max-w-7xl mx-auto w-full">
                <div class="flex items-center gap-3 text-sm font-medium flex-wrap">
                    <NuxtLink to="/blog" class="text-gray-500 hover:text-white transition-colors">База знаний</NuxtLink>
                    <span class="text-gray-700">/</span>
                    <NuxtLink v-if="subcategory.category" :to="`/blog/${subcategory.category.slug}`" class="text-gray-500 hover:text-white transition-colors">
                        {{ subcategory.category.name }}
                    </NuxtLink>
                    <span class="text-gray-700">/</span>
                    <span class="text-orange-400">{{ subcategory.name }}</span>
                </div>
            </nav>

            <header class="relative z-10 px-6 pt-8 pb-12 max-w-7xl mx-auto w-full animate-fade-in-up">
                <div class="flex flex-col md:flex-row md:items-end justify-between gap-8">
                    <div class="max-w-3xl">
                        <h1 class="text-5xl md:text-6xl font-extrabold text-white tracking-tight mb-4">
                            {{ subcategory.name }}
                        </h1>
                        <p class="text-lg text-gray-400 leading-relaxed">
                            {{ subcategory.description }}
                        </p>
                    </div>

                    <div class="flex gap-6 border-l border-white/10 pl-6 md:pb-2">
                        <div>
                            <div class="text-3xl font-bold text-white">{{ subcategory.articles_count || 0 }}</div>
                            <div class="text-xs text-gray-500 uppercase tracking-wider mt-1">Статей</div>
                        </div>
                    </div>
                </div>
            </header>

            <main class="relative z-10 flex-grow px-4 sm:px-6 lg:px-8 pb-24">
                <div class="max-w-7xl mx-auto">
                    <div v-if="subcategory.articles && subcategory.articles.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">

                        <NuxtLink
                            v-for="(article, index) in subcategory.articles"
                            :key="article.id"
                            :to="`/blog/${categorySlug}/${subSlug}/${article.slug || article.id}`"
                            class="group relative flex flex-col rounded-3xl overflow-hidden bg-[#131416]/50 border border-white/5 hover:border-orange-500/30 transition-all duration-500"
                        >
                            <div class="relative w-full pt-[65%] overflow-hidden bg-[#1A1C1E]">
                                <img
                                    v-if="article.cover_image"
                                    :src="`${siteUrl}/storage/${article.cover_image}`"
                                    class="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                >

                                <div v-if="getTrendStatus(article.views)"
                                     class="absolute top-4 left-4 bg-orange-600/90 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest flex items-center gap-1.5 shadow-[0_0_15px_rgba(234,88,12,0.5)] z-20">
                                    <span class="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                    Тренд
                                </div>

                                <div class="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500"></div>
                            </div>

                            <div class="p-6 flex flex-col flex-grow relative z-10 bg-[#131416]">
                                <div class="flex items-center justify-between mb-3">
                                    <div class="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                        <span>{{ new Date(article.created_at).toLocaleDateString('ru-RU') }}</span>
                                    </div>

                                    <div class="flex items-center gap-1.5 text-[10px] font-bold text-orange-500/80 uppercase">
                                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <span>{{ article.views || 0 }}</span>
                                    </div>
                                </div>

                                <h3 class="text-lg font-bold text-white leading-snug group-hover:text-orange-400 transition-colors line-clamp-2">
                                    {{ article.title }}
                                </h3>
                            </div>
                        </NuxtLink>

                    </div>

                    <div v-else class="text-center py-20 bg-white/5 rounded-[2rem] border border-dashed border-white/10">
                        <p class="text-gray-500">В этой субкатегории пока нет статей.</p>
                    </div>
                </div>
            </main>
        </template>
    </div>
</template>

<style scoped>
@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
    opacity: 0;
}
</style>
